import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { CarouselContext, CarouselProvider, Slide, Slider } from 'pure-react-carousel'
import { FC, forwardRef, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { GENERATOR_BANNER_ID } from '../../../GeneratorBanner'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { RTHeading3Node } from '@prismicio/client'
import { RichTextBlock } from 'prismic-reactjs'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { TestimonialsCarouselData } from '../types'
import { chevronLeftBlueThicker, chevronRightBlueThicker } from '../../../../../assets'
import { fireHeapEvent } from '@dtx-company/true-common/src/utils/heap'
import { prismicCachedImage } from '@app/prismic/src/utils'
import { useVisibilityThreshold } from '@dtx-company/shared-components/src/hooks/useVisibilityThreshold'
import CTAButton from './CTAButton'
import PrismicText from '@app/prismic/src/components/PrismicText/PrismicText'

//sectionId is used for navigation
const TestimonialsCarouselInner = forwardRef<
  HTMLDivElement,
  {
    data: TestimonialsCarouselData
    sectionId?: string
  }
>(({ data, sectionId }, ref): JSX.Element => {
  // Get the carousel context
  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)
  const numberOfSlides = data.items?.length || 0
  const sliderId = useId()
  // Subscribe to changes to the current slide index
  useEffect(() => {
    const onChange = (): void => {
      setCurrentSlide(carouselContext.state.currentSlide)
    }
    carouselContext.subscribe(onChange)
    return () => carouselContext.unsubscribe(onChange)
  }, [carouselContext])

  const headerText = useMemo(
    () => (data.primary?.header?.[0] as RichTextBlock | undefined)?.text,
    [data.primary]
  )
  const handleNextSlide = (): void => {
    const nextSlide = currentSlide === numberOfSlides - 1 ? 0 : currentSlide + 1
    carouselContext.setStoreState({ currentSlide: nextSlide })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Next_Slide', {
      next_slide_index: nextSlide,
      header: headerText
    })
  }

  const handlePrevSlide = (): void => {
    const prevSlide = currentSlide === 0 ? numberOfSlides - 1 : currentSlide - 1
    carouselContext.setStoreState({ currentSlide: prevSlide })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Previous_Slide', {
      prev_slide_index: prevSlide,
      header: headerText
    })
  }

  const handleGoToSlide = (newIndex: number): void => {
    carouselContext.setStoreState({ currentSlide: newIndex })
    fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_Go_To_Slide_button', {
      index: newIndex,
      header: headerText
    })
  }

  return (
    <Box flexDirection="column" ref={ref} id={sectionId} data-testid={sectionId}>
      <PrismicText
        richTextField={data.primary?.header}
        variant="display/large"
        textAlign="center"
        fontSize={{ _: '24px', md: '28px', lg: '32px' }}
        lineHeight={{ _: '30px', lg: '40px' }}
        px={{ _: '22px', md: '133px' }}
      />
      <Spacer mb={{ _: '32px', lg: '52px' }} />
      <Box
        justifyItems="center"
        alignItems="center"
        mx="auto"
        maxWidth={{ _: '1083px', md: '1194px' }}
        width="100%"
        px={{ _: '6px', md: '30px' }}
        display="grid"
        gridTemplateColumns={{ _: '4% 92% 4%', lg: 'auto 1033px auto' }}
      >
        <GenericButton
          data-heap-id="TestimonialsCarouselPrevButton"
          display="inline-flex"
          onClick={handlePrevSlide}
          width={{ _: '20px', md: '40px' }}
          height={{ _: '20px', md: '40px' }}
          aria-label="Previous slide"
        >
          <Icon src={chevronLeftBlueThicker} alt="" aria-hidden="true" width="24px" height="24px" />
        </GenericButton>
        <Slider style={{ width: '100%' }} className="carousel-testid">
          {data.items?.map((item, index) => {
            const ariaLabelId = `${sliderId}-label-${index}`
            const ariaDescriptionId = `${sliderId}-description-${index}`
            return (
              <Slide
                index={index}
                key={index}
                aria-labelledby={ariaLabelId}
                aria-describedby={ariaDescriptionId}
                onFocus={() => {
                  carouselContext.setStoreState({ isPlaying: false })
                }}
                onBlur={() => {
                  carouselContext.setStoreState({ isPlaying: true })
                }}
              >
                <Box
                  key={item.image.url}
                  p={{ _: '20px', md: '25px' }}
                  borderRadius="12px"
                  backgroundColor="secondary.backgroundLight"
                  maxWidth="1033px"
                  verticalAlign="top"
                  display="grid"
                  gridTemplateColumns={{ _: '1fr', md: 'auto 42%' }}
                  data-testid="carousel-slide"
                >
                  <Box
                    flexDirection="column"
                    pl={{ _: '0px', md: '4%', lg: '34px' }}
                    pt={{ _: '0px', md: '4%', lg: '10px' }}
                  >
                    <Image
                      src={prismicCachedImage(item['author-logo'].url ?? '')}
                      alt={item['author-logo'].alt ?? ''}
                      lazyLoad={true}
                      maxHeight={{ _: '34.51px', md: '45.5px', lg: '54px' }}
                      maxWidth={{ _: '83.07px', md: '102px', lg: '135px' }}
                    />
                    <PrismicText
                      id={ariaLabelId}
                      as="h3"
                      richTextField={item.testimonial_title}
                      variant="header/subheader"
                      fontWeight="600"
                      style={{ whiteSpace: 'pre-wrap' }}
                      fontSize={{ _: '14px', md: '16px', lg: '20px' }}
                      lineHeight={{ _: '14.52px', md: '19px', lg: '24px' }}
                      pr={{ _: '0px', md: '52px' }}
                    />
                    <Spacer mb={{ _: '25.6px', md: '40px' }} />
                    <PrismicText
                      id={ariaDescriptionId}
                      richTextField={item.text}
                      variant="body/medium"
                      fontWeight={item.is_body_bold ? '500' : '300'}
                      style={{ whiteSpace: 'pre-wrap' }}
                      fontSize={{ _: '14px', md: '16px', lg: '20px' }}
                      lineHeight={{ _: '14.52px', md: '19px', lg: '24px' }}
                      pr={{ _: '0px', md: '52px' }}
                    />
                    <Spacer mb={['16px', '40px']} />
                    {item.button_label && (
                      <Box>
                        <CTAButton
                          data-heap-id={`TestimonialsCarouselSlide${(
                            item.testimonial_title[0] as RTHeading3Node
                          )?.text.replace(/\s+/g, '')}CTAButton`} // Strip all whitespace
                          variant={item.button_variant ?? 'Primary'}
                          label={item.button_label}
                          href={item.button_href?.url}
                          onClick={() =>
                            fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_slide_button', {
                              index: index,
                              button_label: item.button_label,
                              header: headerText
                            })
                          }
                        />
                      </Box>
                    )}
                    {Boolean(item['additional-text'].length) && (
                      <>
                        <Spacer mb="16px" />
                        <PrismicText
                          richTextField={item['additional-text']}
                          variant="body/medium"
                          fontSize={{ _: '14px', md: '14px', lg: '16px' }}
                          style={{ whiteSpace: 'pre-wrap' }}
                          pr={{ _: '0px', md: '17px' }}
                        />
                      </>
                    )}
                    <Spacer display={{ _: 'block', md: 'none' }} mb="16px" />
                  </Box>
                  <Box justifyContent="center" alignItems={{ _: 'flex-end', md: 'unset' }}>
                    <Image
                      lazyLoad={true}
                      src={prismicCachedImage(item.image.url ?? '')}
                      alt={item.image.alt ?? ''}
                      width="100%"
                    />
                  </Box>
                </Box>
              </Slide>
            )
          })}
        </Slider>
        <GenericButton
          data-heap-id="TestimonialsCarouselNextButton"
          display="inline-flex"
          onClick={handleNextSlide}
          width={{ _: '20px', md: '40px' }}
          height={{ _: '20px', md: '40px' }}
          aria-label="Next slide"
        >
          <Icon
            src={chevronRightBlueThicker}
            alt=""
            aria-hidden="true"
            width="24px"
            height="24px"
          />
        </GenericButton>
      </Box>
      <Spacer mb="24px" />
      <Box width="100%" justifyContent="center">
        {data.items?.map((_, id) => (
          <GenericButton
            onClick={() => handleGoToSlide(id)}
            key={`slide-${id}`}
            bg={id === currentSlide ? 'primary.flowBlue' : 'primary.white'}
            border="1px solid"
            borderColor="primary.flowBlue"
            mx="4px"
            width="8px"
            height="8px"
            borderRadius="50%"
            aria-label={`Go to slide ${id + 1}`}
          />
        ))}
      </Box>
      <Spacer mb="24px" />
      <Box flexDirection="column" alignItems="center">
        <CTAButton
          target="_self"
          data-heap-id="TestimonialsCarouselCTAButton"
          variant={data.primary?.button_variant ?? 'Primary'}
          label={data.primary?.button_label}
          tabIndex={0}
          href={`#${GENERATOR_BANNER_ID}`}
          onClick={e => {
            e.preventDefault()
            fireHeapEvent('Flowcode_LoggedOutGenerator_Clicked_CTAButton', {
              label: data.primary?.button_label,
              button_variant: data.primary?.button_variant
            })
            window.scroll(0, 0)
          }}
        />
      </Box>
      <Spacer mb={{ _: '32px', md: '56px', lg: '96px' }} />
    </Box>
  )
})

TestimonialsCarouselInner.displayName = 'TestimonialsCarousel'

export const TestimonialsCarousel: FC<{
  data?: TestimonialsCarouselData
  sectionId?: string
}> = ({ data, sectionId }): JSX.Element | null => {
  const testimonialsContainerRef = useRef<HTMLDivElement | null>(null)
  const { visible } = useVisibilityThreshold({ threshold: 0.1, ref: testimonialsContainerRef })

  if (!data) return null

  return (
    <CarouselProvider
      totalSlides={data.items?.length || 0}
      naturalSlideWidth={500}
      naturalSlideHeight={200}
      isIntrinsicHeight
      infinite
      interval={9000}
      isPlaying={visible}
      dragEnabled={false}
      visibleSlides={1}
    >
      <TestimonialsCarouselInner data={data} sectionId={sectionId} ref={testimonialsContainerRef} />
    </CarouselProvider>
  )
}

export default TestimonialsCarousel
